import { useInfiniteQuery } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import styled from 'styled-components';

import DebouncedSearchBar from '@/components/DebouncedSearchBar';
import InfiniteLoader from '@/components/InfiniteLoader';
import ScrollableTable from '@/components/ScrollableTable';
import { Table, TableContent, TableHeader, Th } from '@/components/Table';
import Text from '@/components/Text';
import {
  BankAccountChangelogResource,
  PaginatedResponse,
} from '@/types/resources';
import { api } from '@/utils/api';

import BankAccountsChangelogTableRow from './BankAccountsChangelogTableRow';

const StyledTable = styled(Table)`
  td {
    font-size: 12px;
  }
  th,
  td {
    padding: 16px 12px;
    &:first-child {
      padding-left: 24px;
    }
    &:last-child {
      padding-right: 24px;
    }
  }
`;

const BankAccountsChangelogTable = () => {
  const [search, setSearch] = useState('');

  const fetchChangelogs = ({
    pageParam = '/v1/payments/bank-accounts-changelog',
  }) => {
    return api.get<never, PaginatedResponse<BankAccountChangelogResource>>(
      pageParam,
      {
        params: {
          ...(!!search && { search }),
        },
      },
    );
  };

  const {
    data,
    isError,
    isLoading,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
  } = useInfiniteQuery<PaginatedResponse<BankAccountChangelogResource>>(
    ['bank_accounts_changelog', { search }],
    fetchChangelogs,
    {
      getNextPageParam: (lastPage) => lastPage.links.next,
    },
  );

  const changelogs = useMemo(
    () =>
      Object.keys(data?.pages || {}).reduce<BankAccountChangelogResource[]>(
        (acc, currKey: any) => {
          return [...acc, ...data!.pages[currKey].data];
        },
        [],
      ),
    [data],
  );

  return (
    <>
      <DebouncedSearchBar
        setValue={setSearch}
        placeholder="Search by receiver"
      />
      <TableHeader>
        <Text $type="smallHeadline" $color="gray">
          CHANGELOG LIST
        </Text>
      </TableHeader>
      <ScrollableTable>
        <StyledTable>
          <thead>
            <tr>
              <Th>Name</Th>
              <Th>Modified Field</Th>
              <Th>Value Change</Th>
              <Th>Date & Time</Th>
            </tr>
          </thead>
          <tbody>
            <TableContent
              isLoading={isLoading}
              isError={isError}
              hasData={!!changelogs?.length}
            >
              {changelogs.map((changelog) => (
                <BankAccountsChangelogTableRow
                  key={changelog.id}
                  changelog={changelog}
                />
              ))}
            </TableContent>
          </tbody>
        </StyledTable>
      </ScrollableTable>
      <InfiniteLoader
        hasNextPage={hasNextPage}
        fetchNextPage={fetchNextPage}
        isFetchingNextPage={isFetchingNextPage}
      />
    </>
  );
};

export default BankAccountsChangelogTable;
